import { parseLocalDateString } from '../../../helpers/date-utils';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import {
  copyScribeSuggestionMarker,
  getScribeSuggestionMarker,
  markScribeSuggestion,
} from '../../scribe-suggestion-marker.util';

type InputListField = ScribeSuggestionMarkerTarget & {
  id?: unknown;
  type?: unknown;
  value?: unknown;
};
type InputListRow = InputListField[];
type MedicationRow = ScribeSuggestionMarkerTarget & {
  id?: unknown;
  Id?: unknown;
};

const MEDICATION_FIELDS = ['Date', 'Medication', 'Freq', 'Dosage', 'Stop'] as const;

export function preserveInputListScribeMarkers(
  existingRows: unknown,
  nextRows: InputListRow[],
): InputListRow[] {
  const savedRows = Array.isArray(existingRows)
    ? existingRows.filter((row): row is InputListRow => Array.isArray(row))
    : [];
  const matchedRowIndexes = new Set<number>();

  return nextRows.map((nextRow) => {
    const sourceIndex = findMatchingRowIndex(
      savedRows,
      nextRow,
      matchedRowIndexes,
      getInputListId,
      areInputListRowsEqual,
    );
    if (sourceIndex < 0) {
      return nextRow;
    }

    matchedRowIndexes.add(sourceIndex);
    const sourceRow = savedRows[sourceIndex];
    nextRow.forEach((targetField, fieldIndex) => {
      const sourceField = sourceRow[fieldIndex];
      if (
        areInputListFieldsEqual(sourceField, targetField) &&
        hasMeaningfulInputListValue(targetField)
      ) {
        copyScribeSuggestionMarker(sourceField, targetField);
      }
    });

    return nextRow;
  });
}

export function preserveMedicationScribeMarkers(
  existingRows: unknown,
  nextRows: MedicationRow[],
): MedicationRow[] {
  const savedRows = Array.isArray(existingRows) ? existingRows.filter(isMedicationRow) : [];
  const matchedRowIndexes = new Set<number>();

  return nextRows.map((nextRow) => {
    const sourceIndex = findMatchingRowIndex(
      savedRows,
      nextRow,
      matchedRowIndexes,
      getMedicationId,
      areMedicationRowsEqual,
    );
    if (sourceIndex < 0) {
      return nextRow;
    }

    matchedRowIndexes.add(sourceIndex);
    copyMatchingMedicationMarkers(savedRows[sourceIndex], nextRow);
    return nextRow;
  });
}

function findMatchingRowIndex<T>(
  savedRows: T[],
  nextRow: T,
  matchedRowIndexes: Set<number>,
  getId: (row: T) => unknown,
  areRowsEqual: (source: T, target: T) => boolean,
): number {
  const nextId = normalizeId(getId(nextRow));
  if (nextId) {
    const idMatchIndex = savedRows.findIndex(
      (savedRow, index) => !matchedRowIndexes.has(index) && normalizeId(getId(savedRow)) === nextId,
    );
    if (idMatchIndex >= 0) {
      return idMatchIndex;
    }
  }

  return savedRows.findIndex(
    (savedRow, index) =>
      !matchedRowIndexes.has(index) &&
      !normalizeId(getId(savedRow)) &&
      areRowsEqual(savedRow, nextRow),
  );
}

function areInputListRowsEqual(source: InputListRow, target: InputListRow): boolean {
  return (
    source.length === target.length &&
    target.every((field, index) => areInputListFieldsEqual(source[index], field))
  );
}

function getInputListId(row: InputListRow): unknown {
  return row.find((field) => normalizeId(field?.id))?.id;
}

function areInputListFieldsEqual(
  source: InputListField | undefined,
  target: InputListField,
): boolean {
  const isDateField = source?.type === 'date' || target.type === 'date';
  return areValuesEqual(source?.value, target.value, isDateField);
}

function hasMeaningfulInputListValue(field: InputListField): boolean {
  return hasMeaningfulValue(field.value, field.type === 'date');
}

function isMedicationRow(value: unknown): value is MedicationRow {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function getMedicationId(row: MedicationRow): unknown {
  return row.id ?? row.Id;
}

function copyMatchingMedicationMarkers(source: MedicationRow, target: MedicationRow): void {
  const marker = getScribeSuggestionMarker(source);
  if (marker === null) {
    return;
  }

  if (marker === true) {
    MEDICATION_FIELDS.forEach((field) => copyMedicationFieldMarker(source, target, field));
    return;
  }

  Object.keys(marker).forEach((field) => {
    if (isMedicationField(field)) {
      copyMedicationFieldMarker(source, target, field);
    }
  });
}

function copyMedicationFieldMarker(
  source: MedicationRow,
  target: MedicationRow,
  field: (typeof MEDICATION_FIELDS)[number],
): void {
  if (
    areMedicationFieldValuesEqual(source, target, field) &&
    hasMeaningfulValue(target[field], field === 'Date' || field === 'Stop')
  ) {
    markScribeSuggestion(target, field);
  }
}

function areMedicationRowsEqual(source: MedicationRow, target: MedicationRow): boolean {
  return MEDICATION_FIELDS.every((field) => areMedicationFieldValuesEqual(source, target, field));
}

function areMedicationFieldValuesEqual(
  source: MedicationRow,
  target: MedicationRow,
  field: (typeof MEDICATION_FIELDS)[number],
): boolean {
  return areValuesEqual(source[field], target[field], field === 'Date' || field === 'Stop');
}

function isMedicationField(value: string): value is (typeof MEDICATION_FIELDS)[number] {
  return MEDICATION_FIELDS.includes(value as (typeof MEDICATION_FIELDS)[number]);
}

function areValuesEqual(source: unknown, target: unknown, isDateField: boolean): boolean {
  if (isDateField) {
    const sourceDate = parseLocalDateString(source as string);
    const targetDate = parseLocalDateString(target as string);
    if (sourceDate || targetDate) {
      return !!sourceDate && sourceDate === targetDate;
    }
  }

  return normalizeValue(source) === normalizeValue(target);
}

function hasMeaningfulValue(value: unknown, isDateField: boolean): boolean {
  if (isDateField) {
    return !!parseLocalDateString(value as string);
  }

  return normalizeValue(value) !== '';
}

function normalizeValue(value: unknown): string {
  return `${value ?? ''}`.trim();
}

function normalizeId(value: unknown): string {
  return `${value ?? ''}`.trim();
}
